// Pipeline board (prototype): lanes per stage, closed deals, filter "Zu prüfen (Migration)" (spec 10); the search
// field in the header filters the cards.
import { OFFER_PHASE, type OfferStage } from "@pipeline/core";
import type { Deal, Volume } from "../api";
import { isClosed, S, stages } from "../state";
import { $, ago, daysSince, emptyState, esc, fmtDate, fmtKg, fmtTEUR, num, redraw } from "../util";
import { overdue } from "./briefing";
import { markenHtml } from "./volumen";

export function volTrend(v: Volume | null): "up" | "down" | null {
  const h = v?.history ?? [];
  if (h.length < 2) return null;
  const a = h[h.length - 2]!;
  const b = h[h.length - 1]!;
  if (daysSince(b.at) > 14) return null;
  const useEur = num(a.eurPerYear) && num(b.eurPerYear);
  const pa = useEur ? a.eurPerYear : a.kgPerYear;
  const pb = useEur ? b.eurPerYear : b.kgPerYear;
  if (!num(pa) || !num(pb) || pa === pb) return null;
  return pb > pa ? "up" : "down";
}

export function volLine(v: Volume | null): string {
  if (!v || !num(v.kgPerYear)) return `<span class="vol muted">Volumen offen</span>`;
  const t = volTrend(v);
  return `<span class="vol"><b>${fmtKg(v.kgPerYear)}</b>/Jahr${num(v.eurPerYear) ? ` · ${fmtTEUR(v.eurPerYear)}` : ""}${t ? ` <span class="trend ${t}" title="Volumen ${t === "up" ? "gestiegen" : "gesunken"}">${t === "up" ? "▲" : "▼"}</span>` : ""}</span>`;
}

export const OFFER_LABEL: Record<OfferStage, string> = { indikativ: "indikativ", probebatch: "Probebatch", final: "final" };

function card(d: Deal): string {
  const na = d.nextAction;
  const w = d.waitingOn === "us" ? `<span class="pill p-hoch">Wir sind dran</span>` : d.waitingOn === "customer" ? `<span class="pill p-info">Kunde ist dran</span>` : "";
  const h = d.health === "risk" ? `<span class="pill p-hoch">Risiko</span>` : d.health === "watch" ? `<span class="pill p-mittel">Beobachten</span>` : "";
  const review = d.needsReview ? `<span class="pill p-mittel">zu prüfen</span>` : "";
  const offer = d.stage === OFFER_PHASE && d.offerStage ? `<span class="pill p-info" title="Stand des Angebots">${OFFER_LABEL[d.offerStage]}</span>` : "";
  const reason = d.closedReason ? `<span class="chip" title="${esc(d.closedReason)}">${esc(d.closedReason)}</span>` : "";
  return `<button class="card" data-open="${esc(d.id)}">
    <span class="co"><span>${esc(d.company || "–")}</span><span class="mono">${esc(d.ref)}</span></span>
    <span class="nm">${esc(d.name)}</span>
    ${markenHtml({ pharma: d.profil?.pharma ?? false, jahresvolumen: d.jahresvolumen })}
    ${d.product?.form || d.product?.pack ? `<span class="chips">${d.product.form ? `<span class="chip" title="${esc(d.product.form)}">${esc(d.product.form)}</span>` : ""}${d.product.pack ? `<span class="chip" title="${esc(d.product.pack)}">${esc(d.product.pack)}</span>` : ""}</span>` : ""}
    ${volLine(d.volume)}
    ${na?.text && !na.done ? `<span class="na"><span>→ ${esc(na.text)}</span>${na.due ? `<span class="due ${overdue(d) ? "over" : ""}">${fmtDate(na.due)}</span>` : ""}</span>` : ""}
    <span class="ft"><span>${d.owner ? `${esc(d.owner)} · ` : ""}${ago(d.lastActivityAt)}</span><span class="chips">${offer}${reason}${review}${h}${w}</span></span>
  </button>`;
}

export function dealOptions(selected: string, includeNone: boolean): string {
  const ds = [...S.deals.values()].sort((a, b) => a.company.localeCompare(b.company) || a.name.localeCompare(b.name));
  return (
    (includeNone ? `<option value="">– Deal wählen –</option>` : "") +
    ds
      .map((d) => `<option value="${esc(d.id)}" ${d.id === selected ? "selected" : ""}>${esc(d.company)} · ${esc(d.name)} (${esc(d.ref)})${isClosed(d.stage) ? " (abgeschl.)" : ""}</option>`)
      .join("")
  );
}

export function renderBoard(): void {
  const el = $("#v-board");
  if (!S.loaded) {
    el.innerHTML = emptyState("Lade Pipeline …", "");
    return;
  }
  const q = S.q.trim().toLowerCase();
  const all = [...S.deals.values()].filter(
    (d) =>
      (!S.reviewOnly || d.needsReview) &&
      (!q ||
        [d.name, d.company, d.ref, d.owner, d.product?.form, d.product?.pack, d.contacts.map((c) => `${c.name ?? ""} ${c.email ?? ""}`).join(" ")]
          .join(" ")
          .toLowerCase()
          .includes(q)),
  );
  const st = stages().filter((s) => S.showClosed || !s.closed);
  const reviewCount = [...S.deals.values()].filter((d) => d.needsReview).length;
  const shown = all.filter((d) => st.some((s) => s.id === d.stage)).length;
  const toolbar = `<div class="toolbar">${q ? filterNote(S.q, `${shown} ${shown === 1 ? "Deal" : "Deals"}`) : ""}
    <label><input type="checkbox" id="closed" ${S.showClosed ? "checked" : ""}> Abgeschlossene zeigen</label>
    ${reviewCount ? `<label><input type="checkbox" id="review" ${S.reviewOnly ? "checked" : ""}> Zu prüfen (Migration) <span class="mono">${reviewCount}</span></label>` : ""}</div>`;
  if (!S.deals.size) {
    el.innerHTML = toolbar + emptyState("Noch keine Deals", "Sie kommen mit dem HubSpot-Import, oder du legst sie aus Anfragen und offenen Zuordnungen an.");
    bindToolbar();
    return;
  }
  redraw(
    el,
    toolbar +
      `<div class="board-scroll"><div class="board">${st
      .map((s) => {
        const ds = all.filter((d) => d.stage === s.id).sort((a, b) => (b.lastActivityAt ?? "").localeCompare(a.lastActivityAt ?? ""));
        const kgSum = ds.reduce((t, d) => t + (num(d.volume?.kgPerYear) ? d.volume!.kgPerYear! : 0), 0);
        const eurSum = ds.reduce((t, d) => t + (num(d.volume?.eurPerYear) ? d.volume!.eurPerYear! : 0), 0);
        const sum = [kgSum ? `${fmtKg(kgSum)}/Jahr` : "", eurSum ? fmtTEUR(eurSum) : ""].filter(Boolean).join(" · ");
        return `<div class="lane"><div class="lane-h"><b>${esc(s.label)}</b><span>${ds.length}</span></div>${sum ? `<div class="lane-sum">${sum}</div>` : ""}${ds.map(card).join("")}</div>`;
      })
      .join("")}</div></div>`,
    ".board-scroll",
  );
  fitBoard();
  bindToolbar();
}

/**
 * With a mouse the board scrolls inside the window (styles.css), as high as the window leaves below its top: its
 * horizontal scrollbar stays in sight at the lower edge instead of under the longest lane.
 */
export function fitBoard(): void {
  const box = document.querySelector<HTMLElement>("#v-board .board-scroll");
  if (!box) return;
  const top = box.getBoundingClientRect().top + scrollY;
  const below = parseFloat(getComputedStyle(box.closest("main .wrap") ?? box).paddingBottom) || 0;
  $("#v-board").style.setProperty("--board-h", `${Math.max(240, document.documentElement.clientHeight - top - below)}px`);
}

/** What the header search filters the view by, with a way back to everything. */
export const filterNote = (q: string, found: string) =>
  `<span class="qnote">Gefiltert nach „${esc(q.trim())}“: ${esc(found)} <button type="button" class="link" data-clear-search>Filter aufheben</button></span>`;

function bindToolbar(): void {
  const c = document.querySelector<HTMLInputElement>("#closed");
  if (c) c.onchange = () => ((S.showClosed = c.checked), renderBoard());
  const r = document.querySelector<HTMLInputElement>("#review");
  if (r) r.onchange = () => ((S.reviewOnly = r.checked), renderBoard());
}
